import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { ScrollView, StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { Ionicons } from "@expo/vector-icons";
import { arrayMove } from "@dnd-kit/sortable";

import Colors from "../../../../Resources/Color";
import Fonts from "../../../../Resources/Fonts";
import GlobalStyles from "../../../../Resources/Styles";
import CustomAlert from "../../../Components/CustomAlert";
import { useDevice, useScreenDimensions } from "../../../_layout";
import { useToast } from "../../../contexts/ToastContext";
import { useMainViewScroll } from "../../../platform/mainViewScrollContext";
import AddFoodModal from "../../foods/editor/AddFoodModal";
import {
  INITIAL_ADD_FOOD_MODAL_SESSION_STATE,
  reduceAddFoodModalSession,
} from "../../foods/editor/addFoodModalSession";
import { COACH_NUTRITION_BOOTSTRAP_RESOURCES } from "../../coachNutritionBootstrap";
import { useNutritionDragSensors } from "../../shared/useNutritionDragSensors";
import {
  buildCanonicalFoodFilters,
  FOOD_SEARCH_SURFACE_PICKER,
} from "../../foods/foodSearchService";
import { useCanonicalFoodSearch } from "../../foods/useCanonicalFoodSearch";
import NutritionEditorActionsMenu from "../../shared/NutritionEditorActionsMenu";
import NutritionDraftStatusPill from "../../shared/NutritionDraftStatusPill";
import NutritionSyncNotice from "../../shared/NutritionSyncNotice";
import { UI_MACRO_DISPLAY_ORDER } from "../../shared/nutritionFormatters";
import {
  getNutritionEntityDisplayName,
} from "../../shared/nutritionEntityNameUtils";
import { buildNutritionEditorActionItems } from "../../shared/nutritionEditorActions";
import { RecipeEditorSummarySection } from "../../recipes/editor/RecipeEditorSummarySection";
import { NutritionEntitySyncStatus } from "../../../Firestore/nutrition/OptimisticNutritionEntity";
import {
  INGREDIENT_DATABASE_OPTIONS,
  INGREDIENT_SORT_OPTIONS,
  MICRONUTRIENT_FIELDS,
  formatQuantityInputValue,
  getIngredientCommonMeasures,
  getIngredientQuantityInGrams,
  getIngredientSelectedUnitKey,
  getIngredientTotals,
  getMacroCalorieBreakdown,
  getQuantityInGramsFromUnit,
  getQuantityValueFromGrams,
  normalizeDecimalInput,
} from "../../recipes/recipeLogic";
import {
  addEquivalentOptionToFormOptions,
  buildEquivalentOptionFromSelectedFood,
  getEquivalentValidationFeedback,
  getEquivalentDraftValidity,
  isEquivalentDraftSemanticallyEmpty,
  shouldShowEquivalentUnsaveableOptionsToastTransition,
} from "../equivalentLogic";
import {
  getNutritionLimitMessageDescriptor,
  getNutritionLimitMessageVariables,
  shouldShowNutritionLimitWarning,
  validateEquivalentDraftLimits,
} from "../../nutritionLimits";
import { EquivalentEditorDetailsSection } from "./EquivalentEditorDetailsSection";
import { EquivalentOptionsSection } from "./EquivalentOptionsSection";
import { buildEquivalentOptionPickerResults } from "./equivalentPickerResults";
import { useFoodPickerRevealInMainView } from "../../shared/foodPicker/foodPickerReveal";

const SectionCard = ({ icon, title, children, style }) => (
  <View style={[styles.sectionCard, style]}>
    <View style={GlobalStyles.CardHeader}>
      <View style={GlobalStyles.CardHeaderMain}>
        <View style={[GlobalStyles.CardIcon, styles.sectionIconWrap]}>
          <Ionicons name={icon} size={14} color={Colors.grayColor} />
        </View>
        <Text style={[GlobalStyles.CardTitle, styles.sectionTitleUppercase]}>
          {title}
        </Text>
      </View>
    </View>
    {children}
  </View>
);

export const EquivalentEditorView = observer(function EquivalentEditorView({
  coach,
  equivalent,
  isEditing,
  onBack,
  onDuplicate,
  onDelete,
}) {
  const { t, i18n } = useTranslation();
  const { showToast } = useToast();
  const { revealInMainView } = useMainViewScroll();
  const { screenSize } = useScreenDimensions();
  const { isMobile } = useDevice();
  const language = i18n?.resolvedLanguage || i18n?.language || "en";
  const searchInputRef = useRef(null);
  const optionSearchInputPressingRef = useRef(false);
  const pickerPanelRootRef = useRef(null);
  const optionSearchTextRef = useRef("");
  const [optionSearchText, setOptionSearchText] = useState("");
  optionSearchTextRef.current = optionSearchText;
  const [optionSortKey, setOptionSortKey] = useState("default");
  const [optionDatabaseKey, setOptionDatabaseKey] = useState("all");
  const [isFoodPickerOpen, setIsFoodPickerOpen] = useState(() => !Boolean(equivalent?.id));
  const [addFoodModalSession, dispatchAddFoodModalSession] = useReducer(
    reduceAddFoodModalSession,
    INITIAL_ADD_FOOD_MODAL_SESSION_STATE,
  );
  const [selectedOptionId, setSelectedOptionId] = useState(
    equivalent?.options?.[0]?.id ?? null,
  );
  const [microsExpanded, setMicrosExpanded] = useState(false);
  const [isBackButtonHovered, setIsBackButtonHovered] = useState(false);
  const [isDeleteAlertVisible, setIsDeleteAlertVisible] = useState(false);
  const previousHasUnsaveableOptionsRef = useRef(false);
  const [equivalentEntity, setEquivalentEntity] = useState(() =>
    typeof equivalent?.applyDraft === "function" ? equivalent : null,
  );

  const selectSearchInputText = useCallback(() => {
    const selectionEnd = String(optionSearchTextRef.current ?? "").length;

    if (selectionEnd === 0) {
      return;
    }

    const applySelection = () => {
      const input = searchInputRef.current;

      if (!input) {
        return;
      }

      input.setSelectionRange?.(0, selectionEnd);
      input.select?.();
      input.setNativeProps?.({
        selection: { start: 0, end: selectionEnd },
      });
    };

    if (typeof requestAnimationFrame === "function") {
      requestAnimationFrame(applySelection);
      return;
    }

    setTimeout(applySelection, 0);
  }, []);

  const focusOptionSearchInput = useCallback(
    ({ selectText = false, ignorePressGuard = false } = {}) => {
      if (optionSearchInputPressingRef.current && !ignorePressGuard) {
        return;
      }

      searchInputRef.current?.focus?.();

      if (selectText) {
        selectSearchInputText();
      }
    },
    [selectSearchInputText],
  );

  useEffect(() => {
    if (typeof equivalent?.applyDraft === "function") {
      setEquivalentEntity(equivalent);
      return;
    }

    if (
      !equivalent?.id ||
      typeof coach?.nutritionStore?.upsertEquivalentEntity !== "function"
    ) {
      setEquivalentEntity(null);
      return;
    }

    setEquivalentEntity(coach.nutritionStore.upsertEquivalentEntity(equivalent));
  }, [coach, equivalent]);

  useEffect(() => {
    setOptionSearchText("");
    setOptionSortKey("default");
    setOptionDatabaseKey("all");
    setIsFoodPickerOpen(!Boolean(equivalentEntity?.id));
    dispatchAddFoodModalSession({ type: "close" });
    setSelectedOptionId(equivalentEntity?.options?.[0]?.id ?? null);
    setMicrosExpanded(false);
  }, [equivalentEntity?.id, t]);

  const openAddFoodModal = useCallback(() => {
    dispatchAddFoodModalSession({ type: "open" });
  }, []);

  const closeAddFoodModal = useCallback(() => {
    dispatchAddFoodModalSession({ type: "close" });
  }, []);

  useEffect(() => {
    coach?.ensureNutritionResourcesFromCloud?.(
      COACH_NUTRITION_BOOTSTRAP_RESOURCES.equivalentEditor,
    )?.catch?.(() => {});
  }, [coach]);

  const form = equivalentEntity?.draft ?? {
    name: "",
    options: [],
  };
  useFoodPickerRevealInMainView({
    isVisible: isFoodPickerOpen,
    panelRootRef: pickerPanelRootRef,
    revealInMainView,
    focusSearchInput: focusOptionSearchInput,
  });

  useEffect(() => {
    if (form.options.length === 0) {
      if (selectedOptionId !== null) {
        setSelectedOptionId(null);
      }
      return;
    }

    if (!form.options.some((option) => option.id === selectedOptionId)) {
      setSelectedOptionId(form.options[0].id);
    }
  }, [form.options, selectedOptionId]);

  const selectedOption = useMemo(
    () => form.options.find((option) => option.id === selectedOptionId) ?? null,
    [form.options, selectedOptionId],
  );
  const selectedOverviewFoodName = selectedOption?.name?.trim() || null;
  const summaryTotals = useMemo(
    () => getIngredientTotals(selectedOption),
    [selectedOption],
  );
  const draftSaveState = useMemo(() => getEquivalentDraftValidity(form), [form]);
  const validationFeedback = useMemo(
    () => getEquivalentValidationFeedback(draftSaveState),
    [draftSaveState],
  );
  const macroBreakdown = useMemo(
    () => getMacroCalorieBreakdown(summaryTotals),
    [summaryTotals],
  );
  const saveValidationMessage = validationFeedback?.inlineMessageKey
    ? t(validationFeedback.inlineMessageKey)
    : draftSaveState?.limitsValidation?.ok === false
      ? t(
          getNutritionLimitMessageDescriptor(draftSaveState.limitsValidation)?.description,
          getNutritionLimitMessageVariables(draftSaveState.limitsValidation),
        )
      : null;

  useEffect(() => {
    previousHasUnsaveableOptionsRef.current = draftSaveState.hasUnsaveableOptions;
  }, [equivalentEntity?.id]);

  useEffect(() => {
    const shouldToast = shouldShowEquivalentUnsaveableOptionsToastTransition({
      previousHasUnsaveableOptions: previousHasUnsaveableOptionsRef.current,
      nextHasUnsaveableOptions: draftSaveState.hasUnsaveableOptions,
    });

    previousHasUnsaveableOptionsRef.current = draftSaveState.hasUnsaveableOptions;

    if (!shouldToast || !validationFeedback?.toast) {
      return;
    }

    showToast(validationFeedback.toast.titleKey, {
      type: "warning",
      description: validationFeedback.toast.descriptionKey,
    });
  }, [draftSaveState.hasUnsaveableOptions, showToast, validationFeedback]);

  const applyFormUpdate = useCallback(
    (nextFormOrUpdater) => {
      const nextForm =
        typeof nextFormOrUpdater === "function"
          ? nextFormOrUpdater(equivalentEntity.draft)
          : nextFormOrUpdater;

      equivalentEntity.applyDraft(nextForm);
    },
    [equivalentEntity],
  );
  const coachFoods = Array.isArray(coach?.nutritionStore?.sortedFoods)
    ? coach.nutritionStore.sortedFoods
    : [];
  const optionSortOptions = useMemo(
    () =>
      INGREDIENT_SORT_OPTIONS.map((option) => ({
        ...option,
        label: t(option.labelKey),
      })),
    [t],
  );
  const databaseOptions = useMemo(
    () =>
      INGREDIENT_DATABASE_OPTIONS.map((option) => ({
        ...option,
        label: t(option.labelKey),
      })),
    [t],
  );
  const selectedSortOption =
    optionSortOptions.find((option) => option.key === optionSortKey) ||
    optionSortOptions[0];
  const selectedDatabaseOption =
    databaseOptions.find((option) => option.key === optionDatabaseKey) ||
    databaseOptions[0];
  const optionFoodFilters = useMemo(() => {
    if (optionDatabaseKey === "createdByMe") {
      return buildCanonicalFoodFilters({ source: "coach" });
    }

    if (optionDatabaseKey === "catalog") {
      return buildCanonicalFoodFilters({ source: "catalog" });
    }

    return buildCanonicalFoodFilters();
  }, [optionDatabaseKey]);
  const optionFoodSearch = useCanonicalFoodSearch({
    coachFoods,
    query: optionSearchText,
    filters: optionFoodFilters,
    sortField: selectedSortOption?.field,
    pageSize: 8,
    surface: FOOD_SEARCH_SURFACE_PICKER,
    language,
  });
  const isCompactLayout = screenSize === "small";
  const useStackedLayout = screenSize !== "large";
  const hasEditorActions = !isEquivalentDraftSemanticallyEmpty(form);
  const topBarMenuItems = useMemo(
    () =>
      buildNutritionEditorActionItems({
        t,
        includeDuplicate: hasEditorActions && typeof onDuplicate === "function",
        includeDelete: hasEditorActions && typeof onDelete === "function",
      }),
    [hasEditorActions, onDelete, onDuplicate, t],
  );
  const macronutrientsTitle = language?.startsWith("es")
    ? "Macronutrientes"
    : "Macronutrients";
  const summaryMacroItems = useMemo(
    () => {
      const macroItems = {
        protein: {
          key: "protein",
          label: t("protein-string"),
          grams: Number(summaryTotals.protein ?? 0),
          share:
            macroBreakdown.totalCalories > 0
              ? (macroBreakdown.proteinCalories / macroBreakdown.totalCalories) * 100
              : 0,
          color: Colors.proteinColor,
        },
        carbs: {
          key: "carbs",
          label: t("carbs-string"),
          grams: Number(summaryTotals.carbs ?? 0),
          share:
            macroBreakdown.totalCalories > 0
              ? (macroBreakdown.carbsCalories / macroBreakdown.totalCalories) * 100
              : 0,
          color: Colors.carbsColor,
        },
        fats: {
          key: "fats",
          label: t("fats-string"),
          grams: Number(summaryTotals.fats ?? 0),
          share:
            macroBreakdown.totalCalories > 0
              ? (macroBreakdown.fatsCalories / macroBreakdown.totalCalories) * 100
              : 0,
          color: Colors.fatsColor,
        },
      };

      return UI_MACRO_DISPLAY_ORDER.map((key) => macroItems[key]);
    },
    [summaryTotals, macroBreakdown, t],
  );
  const micronutrientSummaryItems = useMemo(
    () =>
      MICRONUTRIENT_FIELDS.map((field) => ({
        ...field,
        label: t(field.labelKey),
        value: Number(summaryTotals.micros?.[field.key] ?? 0),
      })),
    [summaryTotals.micros, t],
  );
  const optionResults = useMemo(
    () => buildEquivalentOptionPickerResults(optionFoodSearch.items),
    [optionFoodSearch.items],
  );

  const updateFormField = useCallback((key, value) => {
    applyFormUpdate((current) => ({
      ...current,
      [key]: value,
    }));
  }, [applyFormUpdate]);

  const updateSelectedOptionQuantity = useCallback((optionId, value) => {
    const normalizedValue = normalizeDecimalInput(value);

    applyFormUpdate((current) => ({
      ...current,
      options: current.options.map((option) =>
        option.id === optionId
          ? {
              ...option,
              quantity: normalizedValue,
              quantity_g: getQuantityInGramsFromUnit(
                normalizedValue,
                getIngredientSelectedUnitKey(option),
                getIngredientCommonMeasures(option),
              ),
            }
          : option,
      ),
    }));
  }, [applyFormUpdate]);

  const changeSelectedOptionUnit = useCallback((optionId, nextUnitKey) => {
    applyFormUpdate((current) => ({
      ...current,
      options: current.options.map((option) => {
        if (option.id !== optionId) {
          return option;
        }

        const currentQuantityInGrams = getIngredientQuantityInGrams(option);

        if (nextUnitKey === "grams") {
          return {
            ...option,
            quantityUnitKey: "grams",
            selected_measure_id: null,
            quantity:
              currentQuantityInGrams > 0
                ? formatQuantityInputValue(currentQuantityInGrams)
                : "",
            quantity_g: currentQuantityInGrams,
          };
        }

        const nextMeasure = getIngredientCommonMeasures(option).find(
          (measure) => measure.measureKey === nextUnitKey,
        );
        const gramsPerMeasure = Number(nextMeasure?.grams ?? 0);

        if (gramsPerMeasure <= 0) {
          return option;
        }

        return {
          ...option,
          quantityUnitKey: nextUnitKey,
          selected_measure_id: nextUnitKey,
          quantity:
            currentQuantityInGrams > 0
              ? formatQuantityInputValue(currentQuantityInGrams / gramsPerMeasure)
              : "",
          quantity_g: currentQuantityInGrams,
        };
      }),
    }));
  }, [applyFormUpdate]);

  const removeOption = useCallback((optionId) => {
    applyFormUpdate((current) => ({
      ...current,
      options: current.options.filter((option) => option.id !== optionId),
    }));
  }, [applyFormUpdate]);

  const sensors = useNutritionDragSensors();

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      applyFormUpdate((current) => {
        const oldIndex = current.options.findIndex((option) => option.id === active.id);
        const newIndex = current.options.findIndex((option) => option.id === over.id);

        return {
          ...current,
          options: arrayMove(current.options, oldIndex, newIndex),
        };
      });
    }
  }, [applyFormUpdate]);

  const addOption = useCallback(
    (food) => {
      const optionToAdd = buildEquivalentOptionFromSelectedFood(food, language, t);
      const currentForm = equivalentEntity?.getDraftSnapshot?.() ?? form;
      const nextForm = {
        ...currentForm,
        options: addEquivalentOptionToFormOptions(currentForm.options, {
          ...optionToAdd,
        }),
      };
      const limitsValidation = validateEquivalentDraftLimits(nextForm);
      if (!limitsValidation.ok) {
        if (shouldShowNutritionLimitWarning(limitsValidation)) {
          const descriptor = getNutritionLimitMessageDescriptor(limitsValidation);
          showToast(descriptor?.title ?? "nutritionLimitReachedTitle-string", {
            type: "warning",
            description:
              descriptor?.description ?? "nutritionLimitReachedDescription-string",
            variables: getNutritionLimitMessageVariables(limitsValidation),
          });
        }
        return;
      }

      applyFormUpdate(nextForm);

      setTimeout(() => {
        focusOptionSearchInput({
          selectText: true,
          ignorePressGuard: true,
        });
      }, 0);
    },
    [
      applyFormUpdate,
      focusOptionSearchInput,
      language,
      showToast,
      t,
      equivalentEntity,
      form,
    ],
  );

  const openFoodPicker = useCallback(() => {
    setOptionSearchText("");
    setIsFoodPickerOpen(true);
  }, []);

  const closeFoodPicker = useCallback(() => {
    setOptionSearchText("");
    setIsFoodPickerOpen(false);
  }, []);

  const handleFoodCreated = useCallback(
    (savedFood) => {
      closeAddFoodModal();
      setOptionSearchText("");
      addOption(savedFood);
    },
    [addOption, closeAddFoodModal],
  );

  const handleRetrySave = useCallback(() => {
    equivalentEntity.saveNow();
  }, [equivalentEntity]);

  const syncNotice = useMemo(() => {
    if (
      equivalentEntity.lastError &&
      equivalentEntity.syncStatus !== NutritionEntitySyncStatus.OFFLINE_PENDING
    ) {
      return {
        tone:
          equivalentEntity.syncStatus === NutritionEntitySyncStatus.ERROR
            ? "error"
            : "warning",
        title: t("autosaveError-string"),
        message: t(
          "nutritionEditorRetryNotice-string",
          "We couldn't save the latest changes. Try saving again.",
        ),
        actionLabel: t("save-string"),
        onAction: handleRetrySave,
      };
    }

    return null;
  }, [
    handleRetrySave,
    equivalentEntity.lastError,
    equivalentEntity.syncStatus,
    t,
  ]);

  const handleBack = useCallback(() => {
    onBack?.();
  }, [onBack]);
  const handleEditorAction = useCallback(
    (actionKey) => {
      if (actionKey === "duplicate") {
        onDuplicate?.();
        return;
      }

      if (actionKey === "delete") {
        setIsDeleteAlertVisible(true);
      }
    },
    [onDuplicate],
  );
  const handleConfirmDelete = useCallback(async () => {
    if (typeof onDelete !== "function") {
      return;
    }

    try {
      await onDelete(equivalentEntity);
    } catch (_error) {
      // Keep the alert open. The parent handler owns user-facing error feedback.
    }
  }, [equivalentEntity, onDelete]);
  const deleteEquivalentName = getNutritionEntityDisplayName(
    "equivalent",
    equivalentEntity,
    language,
  );
  const deleteAlertTitle = `${t("delete-string", "Delete").toUpperCase()} ${t(
    "equivalent-string",
    "equivalent",
  ).toUpperCase()}`;
  const deleteAlertText = `This will permanently delete "${deleteEquivalentName}". This action cannot be undone.`;

  if (!equivalentEntity) {
    return null;
  }

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.contentContainer}
      keyboardShouldPersistTaps="handled"
      showsVerticalScrollIndicator={false}
    >
      <View style={styles.topBar}>
        <TouchableOpacity
          activeOpacity={0.8}
          onPress={handleBack}
          onMouseEnter={!isMobile ? () => setIsBackButtonHovered(true) : undefined}
          onMouseLeave={!isMobile ? () => setIsBackButtonHovered(false) : undefined}
          style={[
            styles.backButton,
            isBackButtonHovered ? styles.backButtonHover : null,
          ]}
        >
          <Ionicons name="arrow-back" size={15} color={Colors.bubbleColor} />
          <Text style={styles.backButtonText}>{t("back-string")}</Text>
        </TouchableOpacity>
        <View style={styles.topBarActions}>
          <NutritionDraftStatusPill session={equivalentEntity} />
          <NutritionEditorActionsMenu
            items={topBarMenuItems}
            onAction={handleEditorAction}
          />
        </View>
      </View>

      <NutritionSyncNotice
        session={equivalentEntity}
        tone={syncNotice?.tone}
        title={syncNotice?.title}
        message={syncNotice?.message}
        actionLabel={syncNotice?.actionLabel}
        onAction={syncNotice?.onAction}
        onRetry={handleRetrySave}
        style={styles.syncNotice}
      />

      <View
        style={[
          styles.editorColumns,
          isCompactLayout ? styles.editorColumnsCompact : null,
        ]}
      >
        <View
          style={[
            styles.editorMainColumn,
            isCompactLayout ? styles.editorMainColumnCompact : null,
          ]}
        >
          <SectionCard
            style={isCompactLayout ? styles.sectionCardCompact : null}
            icon="git-compare-outline"
            title={isEditing ? t("editEquivalent-string") : t("createEquivalent-string")}
          >
            <EquivalentEditorDetailsSection
              t={t}
              form={form}
              updateFormField={updateFormField}
            />
            <EquivalentOptionsSection
              t={t}
              form={form}
              saveValidationMessage={saveValidationMessage}
              sensors={sensors}
              handleDragEnd={handleDragEnd}
              useStackedLayout={useStackedLayout}
              getOptionTotals={getIngredientTotals}
              updateSelectedOptionQuantity={updateSelectedOptionQuantity}
              changeSelectedOptionUnit={changeSelectedOptionUnit}
              removeOption={removeOption}
              searchInputRef={searchInputRef}
              optionSearchInputPressingRef={optionSearchInputPressingRef}
              focusOptionSearchInput={focusOptionSearchInput}
              optionSearchText={optionSearchText}
              setOptionSearchText={setOptionSearchText}
              optionSortOptions={optionSortOptions}
              selectedSortOption={selectedSortOption}
              optionSortKey={optionSortKey}
              setOptionSortKey={setOptionSortKey}
              databaseOptions={databaseOptions}
              selectedDatabaseOption={selectedDatabaseOption}
              optionDatabaseKey={optionDatabaseKey}
              setOptionDatabaseKey={setOptionDatabaseKey}
              optionResults={optionResults}
              optionFoodIsLoading={optionFoodSearch.isLoading}
              optionFoodLoadingSessionId={optionFoodSearch.loadingSessionId}
              language={language}
              addOption={addOption}
              onCreateFood={openAddFoodModal}
              isFoodPickerOpen={isFoodPickerOpen}
              openFoodPicker={openFoodPicker}
              closeFoodPicker={closeFoodPicker}
              selectedOptionId={selectedOptionId}
              setSelectedOptionId={setSelectedOptionId}
              pickerPanelRootRef={pickerPanelRootRef}
            />
          </SectionCard>
        </View>

        <View
          style={[
            styles.editorSummaryColumn,
            isCompactLayout ? styles.editorSummaryColumnCompact : null,
          ]}
        >
          <SectionCard
            style={isCompactLayout ? styles.sectionCardCompact : null}
            icon="analytics-outline"
            title={t("summary-string")}
          >
            <RecipeEditorSummarySection
              t={t}
              isMobile={isMobile}
              recipeTotals={summaryTotals}
              macroBreakdown={macroBreakdown}
              macronutrientsTitle={macronutrientsTitle}
              summaryMacroItems={summaryMacroItems}
              micronutrientSummaryItems={micronutrientSummaryItems}
              microsExpanded={microsExpanded}
              setMicrosExpanded={setMicrosExpanded}
              contextTitle={selectedOverviewFoodName}
              summaryReferenceLabel={
                selectedOption ? t("per100gShort-string") : null
              }
            />
          </SectionCard>
        </View>
      </View>

      <CustomAlert
        isVisible={isDeleteAlertVisible}
        onClose={() => setIsDeleteAlertVisible(false)}
        title={deleteAlertTitle}
        text={deleteAlertText}
        noTranslateText={true}
        action={handleConfirmDelete}
        specialCTA="delete-string"
      />
      {addFoodModalSession.isVisible ? (
        <AddFoodModal
          key={addFoodModalSession.sessionKey}
          isVisible={true}
          onClose={closeAddFoodModal}
          onSaved={handleFoodCreated}
          coach={coach}
          food={null}
        />
      ) : null}
    </ScrollView>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    width: "100%",
    marginTop: 24,
  },
  contentContainer: {
    paddingBottom: 36,
  },
  topBar: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 12,
  },
  backButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 10,
    transition: "opacity 180ms ease-in-out",
  },
  backButtonHover: {
    opacity: 0.8,
  },
  backButtonText: {
    fontSize: 13,
    fontWeight: "500",
    color: Colors.bubbleColor,
  },
  topBarActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  syncNotice: {
    marginBottom: 18,
  },
  secondaryButton: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 10,
    borderWidth: 0.5,
    borderColor: Colors.borderColor,
    backgroundColor: Colors.whiteColor,
  },
  secondaryButtonHovered: {
    backgroundColor: Colors.backgroundColor,
  },
  secondaryButtonText: {
    fontSize: 13,
    fontWeight: "500",
    color: Colors.darkgrayColor,
  },
  primaryButton: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 10,
    backgroundColor: Colors.bubbleColor,
  },
  primaryButtonHovered: {
    opacity: 0.9,
  },
  primaryButtonDisabled: {
    opacity: 0.45,
  },
  primaryButtonText: {
    ...Fonts.medium13,
    color: Colors.whiteColor,
  },
  editorColumns: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 16,
  },
  editorColumnsCompact: {
    flexDirection: "column",
    alignItems: "stretch",
    gap: 12,
  },
  editorMainColumn: {
    flex: 1,
    minWidth: 0,
  },
  editorMainColumnCompact: {
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: "auto",
    width: "100%",
    alignSelf: "stretch",
  },
  editorSummaryColumn: {
    width: 360,
    maxWidth: "40%",
  },
  editorSummaryColumnCompact: {
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: "auto",
    width: "100%",
    maxWidth: "100%",
    alignSelf: "stretch",
  },
  sectionCard: {
    backgroundColor: Colors.whiteColor,
    borderRadius: 14,
    borderWidth: 0.5,
    borderColor: Colors.borderColor,
    padding: 18,
    marginBottom: 16,
  },
  sectionCardCompact: {
    marginBottom: 0,
  },
  sectionIconWrap: {
    width: 30,
    height: 30,
    alignItems: "center",
    justifyContent: "center",
  },
  sectionTitleUppercase: {
    textTransform: "uppercase",
  },
});
